import {JsonToBsonSampleData} from '../../../../../DummyData/ConvertSampleJson';

import React, { useState, useEffect } from 'react';
import { BSON } from 'bson'; // Import the BSON library
import { sampleData } from '../../../../../DummyData/ConvertSampleJson';

// Define types for our component
interface JsonToBsonProps {}

// BSON format options
type BsonFormatOption = 'standard' | 'extended' | 'canonical' | 'relaxed';

// BSON content display options
type DisplayOption = 'hex' | 'base64' | 'inspector';

// Size limit for preview in bytes
const PREVIEW_SIZE_LIMIT = 10000; // 10KB

const JsonToBson: React.FC<JsonToBsonProps> = () => {
  // State for the JSON input
  const [jsonInput, setJsonInput] = useState<string>('');
  // State for the BSON output
  const [bsonOutput, setBsonOutput] = useState<Uint8Array | null>(null);
  // State for BSON preview
  const [bsonPreview, setBsonPreview] = useState<string>('');
  // State for error messages
  const [error, setError] = useState<string>('');
  // State for the file name
  const [fileName, setFileName] = useState<string>('json_data');
  // State for loading indicator
  const [isLoading, setIsLoading] = useState<boolean>(false);
  // State for showing advanced options
  const [showAdvanced, setShowAdvanced] = useState<boolean>(false);
  // State for BSON format
  const [formatOption, setFormatOption] = useState<BsonFormatOption>('standard');
  // State for display option
  const [displayOption, setDisplayOption] = useState<DisplayOption>('hex');
  // State to track if the conversion happened
  const [isBsonGenerated, setIsBsonGenerated] = useState<boolean>(false);
  // State for copied indicator
  const [copied, setCopied] = useState<boolean>(false);
  // State for BSON preview
  const [showPreview, setShowPreview] = useState<boolean>(true);
  // State for file size
  const [fileSize, setFileSize] = useState<string>('');

  // Handle JSON input change
  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setJsonInput(e.target.value);
    setError('');
    setIsBsonGenerated(false);
    setBsonOutput(null);
    setBsonPreview('');
  };

  // Handle file name change
  const handleFileNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFileName(e.target.value);
  };

  // Use example data
  const useExampleData = () => {
    setJsonInput(JSON.stringify(sampleData, null, 2));
    setError('');
    setIsBsonGenerated(false);
    setBsonOutput(null);
    setBsonPreview('');
  };

  // Beautify/Format JSON
  const beautifyJson = () => {
    try {
      if (!jsonInput.trim()) {
        setError('Please enter JSON data to format');
        return;
      }
      
      const parsedJson = JSON.parse(jsonInput);
      const beautified = JSON.stringify(parsedJson, null, 2);
      setJsonInput(beautified);
      setError('');
    } catch (e) {
      setError(`Invalid JSON format: ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  // Validate JSON
  const validateJson = (jsonString: string): boolean => {
    try {
      JSON.parse(jsonString);
      return true;
    } catch (e) {
      return false;
    }
  };

  // Clear all inputs and reset states
  const clearAll = () => {
    setJsonInput('');
    setBsonOutput(null);
    setBsonPreview('');
    setError('');
    setFileName('json_data');
    setFormatOption('standard');
    setDisplayOption('hex');
    setIsBsonGenerated(false);
    setFileSize('');
  };

  // Convert bytes to readable file size
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Convert Uint8Array to hex string for preview
  const bytesToHexString = (bytes: Uint8Array, maxLength: number = PREVIEW_SIZE_LIMIT): string => {
    const chunkSize = 16; // bytes per line
    let result = '';
    
    // Determine how many bytes to process (limited by maxLength)
    const bytesToProcess = Math.min(bytes.length, maxLength);
    
    for (let i = 0; i < bytesToProcess; i += chunkSize) {
      // Add offset (address) at the beginning of the line
      const offset = i.toString(16).padStart(8, '0');
      result += `${offset}: `;
      
      // Process this chunk of bytes
      const chunk = bytes.slice(i, Math.min(i + chunkSize, bytesToProcess));
      
      // Convert each byte to hex
      let hexPart = '';
      let asciiPart = '';
      
      for (let j = 0; j < chunkSize; j++) {
        if (i + j < bytesToProcess) {
          const byte = chunk[j];
          // Add the hex representation
          hexPart += byte.toString(16).padStart(2, '0') + ' ';
          
          // Add the ASCII representation (if printable)
          if (byte >= 32 && byte <= 126) {
            asciiPart += String.fromCharCode(byte);
          } else {
            asciiPart += '.';
          }
        } else {
          // Padding for incomplete line
          hexPart += '   ';
          asciiPart += ' ';
        }
      }
      
      result += hexPart.padEnd(chunkSize * 3, ' ') + ' | ' + asciiPart + '\n';
    }
    
    if (bytes.length > maxLength) {
      result += `\n... (${formatFileSize(bytes.length - maxLength)} more)`;
    }
    
    return result;
  };

  // Convert Uint8Array to base64 string
  const bytesToBase64 = (bytes: Uint8Array): string => {
    const binary = Array.from(bytes).map(byte => String.fromCharCode(byte)).join('');
    return window.btoa(binary);
  };

  // Generate inspector view (JSON-like representation of BSON)
  const generateInspectorView = (bsonBytes: Uint8Array): string => {
    try {
      // Try to decode the BSON back to a JavaScript object
      const decoded = BSON.deserialize(bsonBytes);
      // Format it as JSON with indentation
      return JSON.stringify(decoded, null, 2);
    } catch (e) {
      return `Error generating inspector view: ${e instanceof Error ? e.message : String(e)}`;
    }
  };

  // Convert JSON to BSON
  const convertJsonToBson = () => {
    setError('');
    setIsLoading(true);
    setIsBsonGenerated(false);
    setBsonOutput(null);
    setBsonPreview('');
    
    if (!jsonInput.trim()) {
      setError('Please enter JSON data');
      setIsLoading(false);
      return;
    }

    if (!validateJson(jsonInput)) {
      setError('Invalid JSON format. Please check your input.');
      setIsLoading(false);
      return;
    }

    try {
      const jsonData = JSON.parse(jsonInput);
      
      // Convert JSON to BSON using different format options
      let bsonBytes: Uint8Array;
      
      switch (formatOption) {
        case 'extended':
          // Extended JSON format (handling special MongoDB types)
          bsonBytes = BSON.serialize(BSON.EJSON.parse(JSON.stringify(jsonData)));
          break;
        case 'canonical':
          // Canonical mode (sorted keys)
          const canonicalJson = JSON.parse(JSON.stringify(jsonData, Object.keys(jsonData).sort()));
          bsonBytes = BSON.serialize(canonicalJson);
          break;
        case 'relaxed':
          // Relaxed mode (less strict type handling)
          bsonBytes = BSON.serialize(jsonData);
          break;
        case 'standard':
        default:
          // Standard BSON serialization
          bsonBytes = BSON.serialize(jsonData);
          break;
      }
      
      setBsonOutput(bsonBytes);
      setFileSize(formatFileSize(bsonBytes.length));
      
      // Generate preview based on display option
      switch (displayOption) {
        case 'base64':
          // Base64 representation (for text view)
          setBsonPreview(bytesToBase64(bsonBytes));
          break;
        case 'inspector':
          // Object inspector view (converted back to JSON-like format)
          setBsonPreview(generateInspectorView(bsonBytes));
          break;
        case 'hex':
        default:
          // Hex view (default)
          setBsonPreview(bytesToHexString(bsonBytes));
          break;
      }
      
      setIsBsonGenerated(true);
      setIsLoading(false);
    } catch (e) {
      setError(`Error generating BSON: ${e instanceof Error ? e.message : String(e)}`);
      setIsLoading(false);
    }
  };

  // Update preview when display option changes
  useEffect(() => {
    if (bsonOutput) {
      switch (displayOption) {
        case 'base64':
          setBsonPreview(bytesToBase64(bsonOutput));
          break;
        case 'inspector':
          setBsonPreview(generateInspectorView(bsonOutput));
          break;
        case 'hex':
        default:
          setBsonPreview(bytesToHexString(bsonOutput));
          break;
      }
    }
  }, [displayOption, bsonOutput]);

  // Copy BSON preview to clipboard
  const copyToClipboard = () => {
    navigator.clipboard.writeText(bsonPreview)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => {
        setError('Failed to copy to clipboard');
      });
  };

  // Download BSON file
  const downloadBson = () => {
    if (!bsonOutput) return;
    
    const blob = new Blob([bsonOutput], { type: 'application/bson' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    
    link.href = url;
    link.download = `${fileName || 'json_data'}.bson`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="max-w-6xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">JSON to BSON Converter</h1>
      
      {/* Description */}
      <div className="mb-6 bg-blue-50 p-4 rounded-md border border-blue-100">
        <p className="text-blue-800">
          Convert JSON data to BSON (Binary JSON) format used by MongoDB and other NoSQL databases. Paste your JSON data below to generate BSON.
        </p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Input Section */}
        <div className="flex flex-col h-full">
          <div className="bg-white p-4 rounded-md shadow-sm mb-4 flex-grow">
            <div className="flex justify-between items-center mb-3">
              <label htmlFor="jsonInput" className="font-medium text-gray-700">
                JSON Input
              </label>
              <div className="flex space-x-2">
                <button
                  className="text-sm text-blue-600 hover:text-blue-800"
                  onClick={useExampleData}
                >
                  Use Example
                </button>
                <button
                  className="text-sm text-blue-600 hover:text-blue-800"
                  onClick={beautifyJson}
                >
                  Format JSON
                </button>
                <button
                  className="text-sm text-gray-600 hover:text-gray-800"
                  onClick={() => setJsonInput('')}
                >
                  Clear
                </button>
              </div>
            </div>
            <textarea
              id="jsonInput"
              className="w-full h-80 p-3 border border-gray-300 rounded-md font-mono text-sm focus:ring-blue-500 focus:border-blue-500"
              value={jsonInput}
              onChange={handleInputChange}
              placeholder='Paste your JSON data here, e.g., {"name": "John", "age": 30}'
            />
          </div>
          
          {/* Configuration */}
          <div className="bg-white p-4 rounded-md shadow-sm">
            <div className="mb-4">
              <label htmlFor="fileName" className="block text-sm font-medium text-gray-700 mb-1">
                File Name
              </label>
              <input
                type="text"
                id="fileName"
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                value={fileName}
                onChange={handleFileNameChange}
                placeholder="Enter file name (without extension)"
              />
            </div>
            
            <div className="mb-4">
              <button
                className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                onClick={() => setShowAdvanced(!showAdvanced)}
              >
                <span>{showAdvanced ? 'Hide' : 'Show'} Advanced Options</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className={`h-4 w-4 ml-1 transition-transform ${showAdvanced ? 'transform rotate-180' : ''}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            </div>
            
            {showAdvanced && (
              <div className="space-y-4 p-3 bg-gray-50 rounded-md mb-4">
                <div>
                  <label htmlFor="formatOption" className="block text-sm font-medium text-gray-700 mb-1">
                    BSON Format Option
                  </label>
                  <select
                    id="formatOption"
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    value={formatOption}
                    onChange={(e) => setFormatOption(e.target.value as BsonFormatOption)}
                  >
                    <option value="standard">Standard</option>
                    <option value="extended">Extended JSON</option>
                    <option value="canonical">Canonical (Sorted Keys)</option>
                    <option value="relaxed">Relaxed</option>
                  </select>
                </div>
                
                <div>
                  <label htmlFor="displayOption" className="block text-sm font-medium text-gray-700 mb-1">
                    Preview Display
                  </label>
                  <select
                    id="displayOption"
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    value={displayOption}
                    onChange={(e) => setDisplayOption(e.target.value as DisplayOption)}
                  >
                    <option value="hex">Hex View</option>
                    <option value="base64">Base64</option>
                    <option value="inspector">Inspector (BSON as JSON)</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Choose how to display the binary BSON data in the preview
                  </p>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="showPreview"
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    checked={showPreview}
                    onChange={(e) => setShowPreview(e.target.checked)}
                  />
                  <label htmlFor="showPreview" className="ml-2 block text-sm text-gray-700">
                    Show Preview
                  </label>
                </div>
              </div>
            )}
            
            <div className="mt-4 flex justify-between">
              <button
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                onClick={convertJsonToBson}
                disabled={isLoading}
              >
                {isLoading ? (
                  <div className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Processing...
                  </div>
                ) : (
                  'Convert to BSON'
                )}
              </button>
              
              <button
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                onClick={clearAll}
              >
                Clear All
              </button>
            </div>
          </div>
        </div>
        
        {/* Output Section */}
        <div className="bg-white p-4 rounded-md shadow-sm h-full flex flex-col">
          <div className="flex justify-between items-center mb-3">
            <label className="font-medium text-gray-700">
              BSON Output
            </label>
            
            {isBsonGenerated && (
              <div className="flex space-x-2">
                <button
                  className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                  onClick={copyToClipboard}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 mr-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  {copied ? 'Copied!' : 'Copy Preview'}
                </button>
                
                <button
                  className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                  onClick={downloadBson}
                  disabled={!bsonOutput}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 mr-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Download BSON
                </button>
              </div>
            )}
          </div>
          
          <div className="flex-grow flex flex-col">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-md mb-3">
                <p className="font-medium">Error</p>
                <p>{error}</p>
              </div>
            )}
            
            {!error && !isBsonGenerated && (
              <div className="bg-gray-50 border border-gray-200 text-gray-500 p-4 rounded-md flex-grow flex items-center justify-center">
                <div className="text-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-gray-400 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                  <p>
                    BSON output will appear here after conversion.
                  </p>
                </div>
              </div>
            )}
            
            {!error && isBsonGenerated && (
              <div className="flex-grow flex flex-col">
                {showPreview ? (
                  <div className="mb-3">
                    <div className="mb-2 flex justify-between items-center">
                      <h3 className="font-medium text-gray-800">Preview:</h3>
                      <div className="flex items-center">
                        <span className="text-xs text-gray-600 mr-3">File size: {fileSize}</span>
                        <button
                          className="text-xs text-gray-600 hover:text-gray-800"
                          onClick={() => setShowPreview(false)}
                        >
                          Hide Preview
                        </button>
                      </div>
                    </div>
                    <div className="border border-gray-200 rounded-md h-48 overflow-auto p-3 mb-3 font-mono text-xs">
                      {displayOption === 'hex' ? (
                        <pre className="whitespace-pre">{bsonPreview}</pre>
                      ) : displayOption === 'inspector' ? (
                        <pre className="whitespace-pre-wrap">{bsonPreview}</pre>
                      ) : (
                        <div className="break-all">{bsonPreview}</div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="mb-3">
                    <div className="flex justify-between items-center">
                      <button
                        className="text-xs text-blue-600 hover:text-blue-800"
                        onClick={() => setShowPreview(true)}
                      >
                        Show Preview
                      </button>
                      <span className="text-xs text-gray-600">File size: {fileSize}</span>
                    </div>
                  </div>
                )}
                
                <div className="flex-grow flex flex-col">
                  <label htmlFor="bsonInfo" className="text-sm font-medium text-gray-700 mb-1">
                    BSON Information:
                  </label>
                  <div
                    id="bsonInfo"
                    className="w-full flex-grow p-3 border border-gray-300 rounded-md text-xs focus:ring-blue-500 focus:border-blue-500 bg-gray-50"
                  >
                    <p className="mb-2">
                      <strong>Format:</strong> {formatOption.charAt(0).toUpperCase() + formatOption.slice(1)} BSON
                    </p>
                    <p className="mb-2">
                      <strong>Size:</strong> {fileSize}
                    </p>
                    <p className="mb-2">
                      <strong>File name:</strong> {fileName}.bson
                    </p>
                    <p>
                      BSON is a binary format. Use the Download button to save the file for use with MongoDB or other 
                      BSON-compatible systems.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
          
          {isBsonGenerated && (
            <div className="mt-4 bg-green-50 border border-green-200 text-green-700 p-3 rounded-md">
              <div className="flex items-center mb-2">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <p className="font-medium">BSON Successfully Generated!</p>
              </div>
              <p className="text-sm">
                The BSON file is ready for download. You can use this file with MongoDB or any other system that supports BSON format.
              </p>
            </div>
          )}
        </div>
      </div>
      
      {/* Information Section */}
      <div className="mt-8 bg-white p-6 rounded-md shadow-sm">
        <h2 className="text-lg font-semibold mb-4">About JSON to BSON Conversion</h2>
        
        <div className="space-y-4">
          <div>
            <h3 className="font-medium text-gray-800 mb-2">What is BSON?</h3>
            <p className="text-gray-600">
              BSON (Binary JSON) is a binary-encoded serialization format used to store documents and make remote procedure calls in MongoDB.
              It extends the JSON model to provide additional data types, ordered fields, and to be efficient for encoding and decoding within different languages.
            </p>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">Features</h3>
            <ul className="list-disc pl-5 text-gray-600">
              <li><strong>Binary Format</strong> - More compact and efficient than text-based JSON</li>
              <li><strong>Multiple View Options</strong> - Hex view, Base64, and Inspector for examining the BSON data</li>
              <li><strong>BSON Format Options</strong> - Standard, Extended, Canonical, and Relaxed modes for different use cases</li>
              <li><strong>MongoDB Compatible</strong> - Generated BSON files work with MongoDB and other BSON-compatible databases</li>
              <li><strong>File Size Information</strong> - See the size of your BSON data compared to the original JSON</li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">BSON Format Options Explained</h3>
            <ul className="list-disc pl-5 text-gray-600">
              <li><strong>Standard</strong> - Basic BSON serialization with normal type conversion</li>
              <li><strong>Extended JSON</strong> - Supports MongoDB-specific data types (ObjectId, Decimal128, etc.)</li>
              <li><strong>Canonical</strong> - Sorts object keys for consistent output regardless of input order</li>
              <li><strong>Relaxed</strong> - Less strict typing, useful for simpler documents or when exact types aren't crucial</li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">Tips for Better Results</h3>
            <ul className="list-disc pl-5 text-gray-600">
              <li>Use the "Format JSON" button to properly format and validate your JSON before conversion</li>
                <li>Check the BSON preview to ensure the data looks correct before downloading</li>
                <li>Use the "Show Preview" option to see the BSON data in different formats (Hex, Base64, Inspector)</li>
                <li>For large JSON files, consider breaking them into smaller chunks for easier conversion</li>
                <li>Use the "Download BSON" button to save the generated BSON file for use with MongoDB or other systems</li>
            </ul>
            </div>
        </div>
        </div>
    </div>
    );
};
export default JsonToBson;